import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, CheckCircle2, XCircle, ArrowUpDown, FileText, BarChart3, Clock, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface TopicGap {
  grade_level: string;
  subject: string;
  topic: string;
  current_count: number;
  target: number;
  gap: number;
}

interface TopicResult {
  grade_level: string;
  subject: string;
  topic: string;
  generated: number;
  validated: number;
  duplicates: number;
  saved: number;
  failed: number;
  types_generated: Record<number, number>;
  errors: string[];
}

interface RunData {
  id: number;
  created_at: string;
  status: string;
  run_number?: number;
  total_saved?: number;
  total_failed?: number;
  topics_processed?: number;
  total_topics_available?: number;
  cleanup?: Array<{ grade_level: string; subject: string; from_topic: string; to_topic: string | null; questions_moved: number }>;
  results?: TopicResult[];
  global_type_distribution?: Record<number, number>;
}

interface DigestResponse {
  current_coverage: {
    gaps_remaining: number;
    total_questions_needed: number;
    grades_covered: string[];
    top_gaps: TopicGap[];
  };
  runs: RunData[];
}

const TYPE_LABELS: Record<number, string> = {
  0: "Multi-Part",
  1: "Single Choice",
  2: "Short Answer",
  3: "Multi Answer",
  4: "Table Grid",
  5: "Long Answer",
  6: "Ordering",
  7: "Matching",
  8: "Categorising",
};

function TypeDistributionBar({ distribution }: { distribution: Record<number, number> }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (total === 0) return <span className="text-sm text-muted-foreground">No data</span>;

  const colors: Record<number, string> = {
    1: "bg-blue-500",
    2: "bg-green-500",
    3: "bg-purple-500",
    4: "bg-orange-500",
    5: "bg-pink-500",
    6: "bg-yellow-500",
    7: "bg-cyan-500",
    8: "bg-red-500",
  };

  return (
    <div className="space-y-2">
      <div className="h-4 rounded-full overflow-hidden flex">
        {Object.entries(distribution)
          .filter(([, count]) => count > 0)
          .sort(([, a], [, b]) => b - a)
          .map(([type, count]) => (
            <div
              key={type}
              className={`${colors[Number(type)] || "bg-gray-400"} transition-all`}
              style={{ width: `${(count / total) * 100}%` }}
              title={`${TYPE_LABELS[Number(type)] || `Type ${type}`}: ${count} (${Math.round((count / total) * 100)}%)`}
            />
          ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(distribution)
          .filter(([, count]) => count > 0)
          .sort(([, a], [, b]) => b - a)
          .map(([type, count]) => (
            <span key={type} className="text-xs text-muted-foreground">
              <span className={`inline-block w-2 h-2 rounded-full ${colors[Number(type)] || "bg-gray-400"} mr-1`} />
              {TYPE_LABELS[Number(type)] || `Type ${type}`}: {count} ({Math.round((count / total) * 100)}%)
            </span>
          ))}
      </div>
    </div>
  );
}

export default function AdminQuestionGeneration() {
  const { toast } = useToast();
  const [triggerLoading, setTriggerLoading] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<DigestResponse>({
    queryKey: ["/api/admin/quizzative/generation-digest"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/quizzative/generation-digest?history=5");
      return res.json();
    },
  });

  const handleTriggerRun = async () => {
    setTriggerLoading(true);
    try {
      const res = await apiRequest("POST", "/api/admin/quizzative/generation-trigger");
      const result = await res.json();
      toast({
        title: "Generation triggered",
        description: `Saved ${result.total_saved || 0} questions across ${result.topics_processed || 0} topics.`,
      });
      refetch();
    } catch (err) {
      toast({
        title: "Trigger failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setTriggerLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-muted-foreground">Failed to load generation digest.</p>
          <p className="text-sm text-muted-foreground mt-1">{error?.message || "Unknown error"}</p>
          <Button variant="outline" className="mt-4" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { current_coverage, runs } = data;
  const latestRun = runs[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Question Generation Automation</h1>
          <p className="text-muted-foreground">
            Automated AI question generation running 3x/week (Sun, Wed, Sat at 10PM Jamaica time)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={handleTriggerRun} disabled={triggerLoading}>
            {triggerLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            Trigger Run Now
          </Button>
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3x / week</div>
            <p className="text-xs text-muted-foreground">Sun, Wed, Sat at 10PM</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Coverage Gaps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{current_coverage.gaps_remaining}</div>
            <p className="text-xs text-muted-foreground">
              {current_coverage.total_questions_needed} questions needed to fill
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Grades Covered</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {current_coverage.grades_covered.map((g) => (
                <Badge key={g} variant="secondary">{g}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Last Run</CardTitle>
          </CardHeader>
          <CardContent>
            {latestRun ? (
              <>
                <div className="text-2xl font-bold">{latestRun.total_saved || 0} saved</div>
                <p className="text-xs text-muted-foreground">
                  {new Date(latestRun.created_at).toLocaleString("en-JM", { timeZone: "America/Jamaica", hour12: true, year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </p>
              </>
            ) : (
              <span className="text-muted-foreground">No runs yet</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Global type distribution */}
      {latestRun?.global_type_distribution && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Question Type Distribution (AI Generated)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TypeDistributionBar distribution={latestRun.global_type_distribution} />
          </CardContent>
        </Card>
      )}

      {/* Top coverage gaps */}
      {current_coverage.top_gaps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowUpDown className="h-5 w-5" /> Top Coverage Gaps (Below Minimum)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Grade</th>
                    <th className="text-left py-2 px-3 font-medium">Subject</th>
                    <th className="text-left py-2 px-3 font-medium">Topic</th>
                    <th className="text-right py-2 px-3 font-medium">Current</th>
                    <th className="text-right py-2 px-3 font-medium">Target</th>
                    <th className="text-right py-2 px-3 font-medium">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {current_coverage.top_gaps.map((gap, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 px-3">{gap.grade_level}</td>
                      <td className="py-2 px-3">{gap.subject}</td>
                      <td className="py-2 px-3">{gap.topic}</td>
                      <td className="py-2 px-3 text-right">{gap.current_count}</td>
                      <td className="py-2 px-3 text-right">{gap.target}</td>
                      <td className="py-2 px-3 text-right font-medium text-red-500">-{gap.gap}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Run history */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" /> Run History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {runs.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No generation runs yet.</p>
          ) : (
            runs.map((run) => (
              <RunCard key={run.id} run={run} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RunCard({ run }: { run: RunData }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {run.status === "success" ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <XCircle className="h-5 w-5 text-red-500" />
          )}
          <div>
            <span className="font-medium">
              Run #{run.run_number || run.id}
            </span>
            <span className="text-sm text-muted-foreground ml-2">
              {new Date(run.created_at).toLocaleString("en-JM", { timeZone: "America/Jamaica", hour12: true, year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="default">{run.total_saved || 0} saved</Badge>
          {(run.total_failed || 0) > 0 && (
            <Badge variant="destructive">{run.total_failed} failed</Badge>
          )}
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            <FileText className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {expanded && run.results && (
        <div className="mt-3 space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1 px-2 font-medium">Grade</th>
                  <th className="text-left py-1 px-2 font-medium">Subject</th>
                  <th className="text-left py-1 px-2 font-medium">Topic</th>
                  <th className="text-right py-1 px-2 font-medium">Gen</th>
                  <th className="text-right py-1 px-2 font-medium">Valid</th>
                  <th className="text-right py-1 px-2 font-medium">Dupes</th>
                  <th className="text-right py-1 px-2 font-medium">Saved</th>
                </tr>
              </thead>
              <tbody>
                {run.results.map((r, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1 px-2">{r.grade_level}</td>
                    <td className="py-1 px-2">{r.subject}</td>
                    <td className="py-1 px-2">{r.topic}</td>
                    <td className="py-1 px-2 text-right">{r.generated}</td>
                    <td className="py-1 px-2 text-right">{r.validated}</td>
                    <td className="py-1 px-2 text-right">{r.duplicates}</td>
                    <td className="py-1 px-2 text-right font-medium">{r.saved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {run.cleanup && run.cleanup.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Topic cleanup: {run.cleanup.length} topic(s) reassigned
            </div>
          )}
        </div>
      )}
    </div>
  );
}
